import { useTranslation } from '@silospot/i18n/react';
import type { BlockAction, BlockScope } from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { Badge, Btn, Segmented } from './kit';

export interface BlocklistPrefill {
  ip?: string;
  /** Neutral connection class of the row, to drive the residential warning. */
  connType?: string;
  proxyProbable?: boolean;
}

const TTL_OPTIONS = [0, 7, 30, 90];
// IPv4 exact, or IPv6 exact / CIDR /48–/128 (a /64 is one household; /48 its pool).
// IPv4 ranges stay out of the quick path (advanced impact estimate required).
const IP_RE = /^(\d{1,3}\.){3}\d{1,3}$|^[0-9a-f:]+(\/(4[89]|5\d|6[0-4]|128))?$/i;

export function BlocklistModal({
  projectId,
  prefill,
  onClose,
}: {
  projectId: string;
  prefill: BlocklistPrefill;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [ip, setIp] = useState(prefill.ip ?? '');
  const [action, setAction] = useState<BlockAction>('challenge');
  const [scope, setScope] = useState<BlockScope>('site');
  const [ttlDays, setTtlDays] = useState(0);
  const [reason, setReason] = useState('');

  // Only IPv4 CIDRs are the "advanced range" case; IPv6 /48–/128 pass the quick path.
  const isRange = ip.includes('/') && !ip.includes(':');
  const isResidential = prefill.connType === 'residential' || prefill.connType === 'mobile';
  const validIp = IP_RE.test(ip.trim()) && !isRange;

  const mutate = useMutation({
    mutationFn: () =>
      api.addBlocklist(projectId, {
        ip: ip.trim(),
        action,
        scope,
        reason: reason || undefined,
        ttlDays,
      }),
    onSuccess: (res) => {
      toast.success(res.enforced ? t('blocklist.blocked') : t('blocklist.prepared'));
      void qc.invalidateQueries({ queryKey: ['cld-blocklist', projectId] });
      void qc.invalidateQueries({ queryKey: ['cf-status', projectId] });
      onClose();
    },
    onError: (e) => {
      const status = e instanceof ApiError ? e.status : 0;
      toast.error(
        status === 422
          ? t('blocklist.refusedProtected')
          : status === 502
            ? t('blocklist.cfError')
            : t('blocklist.rangeAdvanced'),
      );
    },
  });

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-5">
      <button
        type="button"
        aria-label={t('blocklist.cancel')}
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(15,21,32,0.42)] backdrop-blur-[3px]"
      />
      {/* biome-ignore lint/a11y/useSemanticElements: custom-animated panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('blocklist.add')}
        className="relative max-h-[90vh] w-[460px] max-w-full overflow-y-auto rounded-2xl bg-surface p-6 shadow-[0_24px_60px_rgba(16,24,40,0.3)]"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[16px] font-bold tracking-[-0.01em] text-text">
            {t('blocklist.add')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('blocklist.cancel')}
            className="grid h-8 w-8 place-items-center rounded-md text-text-2 hover:bg-bg"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-4 rounded-md border border-accent-border bg-accent-bg px-3 py-2 text-[11.5px] leading-snug text-accent-strong">
          {t('blocklist.realNote')}
        </p>

        <Label>{t('blocklist.ip')}</Label>
        <input
          value={ip}
          onChange={(e) => setIp(e.target.value)}
          placeholder="185.0.44.10"
          className="w-full rounded-[9px] border border-border-strong px-3 py-2.5 font-mono text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-[rgba(37,99,235,0.12)]"
        />
        <p className="mt-1 text-[11px] text-text-3">{t('blocklist.individualOnly')}</p>
        {isRange ? (
          <p className="mt-1 text-[11.5px] text-fraud-fg">{t('blocklist.rangeAdvanced')}</p>
        ) : null}

        <div className="mt-4 flex items-center justify-between">
          <Label>{t('blocklist.action')}</Label>
          <Segmented
            size="sm"
            value={action}
            onChange={setAction}
            options={[
              { value: 'challenge', label: t('blocklist.challenge') },
              { value: 'block', label: t('blocklist.block') },
            ]}
          />
        </div>
        {isResidential ? (
          <p className="mt-2 flex items-start gap-1.5 rounded-md bg-suspect-bg px-2.5 py-2 text-[11.5px] text-suspect-fg">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            {t('blocklist.warnResidential')}
          </p>
        ) : null}

        <div className="mt-4 flex items-center justify-between">
          <Label>{t('blocklist.scope')}</Label>
          <Segmented
            size="sm"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'site', label: t('blocklist.scopeSite') },
              { value: 'all', label: t('blocklist.scopeAll') },
            ]}
          />
        </div>

        <div className="mt-4">
          <Label>{t('blocklist.ttl')}</Label>
          <div className="flex flex-wrap gap-1.5">
            {TTL_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setTtlDays(d)}
                className={`rounded-md border px-3 py-1.5 text-[12.5px] font-medium ${ttlDays === d ? 'border-text bg-text text-white' : 'border-border bg-surface text-text-2 hover:border-border-strong'}`}
              >
                {d === 0 ? t('blocklist.ttlPermanent') : `${d} ${t('blocklist.days')}`}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Label>{t('blocklist.reason')}</Label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('blocklist.reasonPlaceholder')}
            className="w-full rounded-[9px] border border-border-strong px-3 py-2.5 text-[13px] outline-none focus:border-primary focus:ring-2 focus:ring-[rgba(37,99,235,0.12)]"
          />
        </div>

        <div className="mt-5 flex items-center justify-between">
          <Badge tone={action === 'challenge' ? 'suspect' : 'fraud'}>
            {action === 'challenge' ? t('blocklist.challenge') : t('blocklist.block')}
          </Badge>
          <div className="flex gap-2">
            <Btn onClick={onClose}>{t('blocklist.cancel')}</Btn>
            <Btn primary disabled={!validIp || mutate.isPending} onClick={() => mutate.mutate()}>
              {t('blocklist.confirm')}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }): ReactElement {
  return <div className="mb-1.5 text-[12.5px] font-semibold text-text-2">{children}</div>;
}
